import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { setSupportSession, useSupportSession } from '@/components/platform/supportSession';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  MAX_SUPPORT_ACCESS_MINUTES,
  MIN_SUPPORT_ACCESS_MINUTES,
  MIN_SUPPORT_REASON_LENGTH,
  NON_IMPERSONABLE_ROLE_CODES,
  type RequestSupportAccessRequest,
  type SupportAccessGrant,
  type SupportAccessMode,
  type SupportAccessStatus,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-17 — controlled support access.
 *
 * The property this screen exists to preserve is that support never acts inside a
 * customer's institution without that being visible at the time and
 * reconstructible afterwards. Three things follow.
 *
 * READ-ONLY IS THE DEFAULT AND IT IS PRESELECTED. Choosing impersonation is a
 * deliberate act, not the path of least resistance.
 *
 * IMPERSONATION OF FINANCIAL APPROVERS AND PLATFORM ADMINISTRATORS IS NOT OFFERED.
 * The server refuses it outright, with no override flag anywhere, because
 * impersonating someone who can approve money produces a payment record that says
 * they approved it and no amount of logging on this side makes the finance
 * system's record less wrong. This screen therefore states the refusal as a rule
 * rather than presenting it as something worth trying: an "ask anyway" button
 * teaches people the rule is negotiable, which is the exact failure the rule
 * exists to prevent. If a field operation genuinely cannot function under this,
 * that argument belongs in the open with the policy — not in a flag.
 *
 * A LIVE SESSION IS ANNOUNCED EVERYWHERE. Starting a session installs a banner
 * above the router (see ImpersonationBanner) that no route can unmount and no
 * control can dismiss.
 */

const STATUS_TONES: Record<SupportAccessStatus, string> = {
  PENDING_APPROVAL: 'warnTag',
  APPROVED: 'okTag',
  ACTIVE: 'dangerTag',
  DENIED: 'mutedTag',
  REVOKED: 'mutedTag',
  EXPIRED: 'mutedTag',
};

export function SupportAccessPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [params, setParams] = useSearchParams();
  const institutionId = params.get('institutionId') ?? '';
  const session = useSupportSession();

  const fetcher = useCallback(
    () => platformApi.supportAccess.list(institutionId || undefined),
    [institutionId],
  );
  const { state, reload } = useAsyncData<SupportAccessGrant[]>(`support-access:${institutionId}`, fetcher);

  const [requestOpen, setRequestOpen] = useState(false);
  const [denying, setDenying] = useState<SupportAccessGrant | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const act = async (grant: SupportAccessGrant, action: 'approve' | 'start' | 'end') => {
    setBusyId(grant.id);
    setRowError(null);
    try {
      if (action === 'approve') {
        await platformApi.supportAccess.approve(grant.id);
      } else if (action === 'start') {
        setSupportSession(await platformApi.supportAccess.start(grant.id));
      } else {
        await platformApi.supportAccess.end(grant.id);
        // Clearing the banner is tied to the grant that raised it: ending a
        // different grant must not take the banner down for a session that is
        // still live.
        if (session?.grantId === grant.id) setSupportSession(null);
      }
      reload();
    } catch (cause) {
      setRowError((cause as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.support.title')}</h1>
          <p className={styles.pageLead}>{t('platform.support.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setRequestOpen(true)}>
          <Icon name="plus" size={16} />
          {t('platform.support.request')}
        </button>
      </header>

      {session && (
        <Panel title={t('platform.support.activeTitle')} icon="warning" emphasis="attention">
          <p className={styles.rowDetail}>
            {t('platform.support.activeBody', {
              mode: t(`platform.support.mode.${session.mode}`),
              until: formatDateTime(session.expiresAt, locale),
            })}
          </p>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={() => act({ id: session.grantId } as SupportAccessGrant, 'end')}
          >
            {t('platform.support.end')}
          </button>
        </Panel>
      )}

      <Panel title={t('platform.support.rulesTitle')} icon="noAccess">
        <ul className={styles.ruleList}>
          <li className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t('platform.support.rule.financial', { roles: NON_IMPERSONABLE_ROLE_CODES.join(', ') })}</span>
          </li>
          <li className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t('platform.support.rule.platformAdmin')}</span>
          </li>
          <li className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t('platform.support.rule.separateApprover')}</span>
          </li>
          <li className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t('platform.support.rule.expiry', { max: MAX_SUPPORT_ACCESS_MINUTES })}</span>
          </li>
          <li className={styles.ruleItem}>
            <Icon name="ban" size={14} aria-hidden="true" />
            <span>{t('platform.support.rule.banner')}</span>
          </li>
        </ul>
      </Panel>

      <div className={styles.filterBar} role="search">
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="support-institution">
            {t('platform.support.institutionLabel')}
          </label>
          {/* Picking applies immediately — no UUID to paste, no submit to press. */}
          <InstitutionPicker
            id="support-institution"
            value={institutionId}
            clearable
            onChange={(next) => setParams(next ? { institutionId: next } : {})}
          />
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.support.emptyTitle')}
        emptyBody={
          institutionId ? t('platform.support.emptyInstitution') : t('platform.support.emptyPending')
        }
      >
        {(data) => (
          <ul className={styles.rowList}>
            {data.map((grant) => (
              <li key={grant.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    {t(`platform.support.mode.${grant.mode}`, { defaultValue: grant.mode })}
                    {' — '}
                    {grant.institutionName ?? grant.institutionId}
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t('platform.support.by', { who: grant.supportPersonName ?? grant.supportPersonId })}
                    {grant.targetPersonId
                      ? ` · ${t('platform.support.target', { who: grant.targetPersonName ?? grant.targetPersonId })}`
                      : ''}
                    {` · ${t('platform.support.duration', { minutes: grant.requestedDurationMinutes })}`}
                    {grant.ticketReference ? ` · ${grant.ticketReference}` : ''}
                    {grant.expiresAt
                      ? ` · ${t('platform.support.expires', { when: formatRelative(grant.expiresAt, locale) })}`
                      : ''}
                  </p>
                  <p className={styles.rowDetail}>{grant.reason}</p>
                  {grant.denialReason && <p className={styles.rowDetailError}>{grant.denialReason}</p>}
                  {typeof grant.actionCount === 'number' && grant.actionCount > 0 && (
                    <p className={styles.rowDetail}>
                      {t('platform.support.actions', { n: grant.actionCount })}
                    </p>
                  )}
                </div>
                <div className={styles.rowAside}>
                  <span className={styles[STATUS_TONES[grant.status] ?? 'mutedTag']}>
                    {t(`platform.support.status.${grant.status}`, { defaultValue: grant.status })}
                  </span>
                  {grant.status === 'PENDING_APPROVAL' && (
                    <>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        disabled={busyId === grant.id}
                        onClick={() => act(grant, 'approve')}
                      >
                        {t('platform.support.approve')}
                      </button>
                      <button
                        type="button"
                        className={styles.linkBtnDanger}
                        disabled={busyId === grant.id}
                        onClick={() => setDenying(grant)}
                      >
                        {t('platform.support.deny')}
                      </button>
                    </>
                  )}
                  {grant.status === 'APPROVED' && (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      disabled={busyId === grant.id}
                      onClick={() => act(grant, 'start')}
                    >
                      {t('platform.support.start')}
                    </button>
                  )}
                  {grant.status === 'ACTIVE' && (
                    <button
                      type="button"
                      className={styles.linkBtnDanger}
                      disabled={busyId === grant.id}
                      onClick={() => act(grant, 'end')}
                    >
                      {t('platform.support.end')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>

      {rowError && (
        <p className={styles.formError} role="alert">
          {rowError}
        </p>
      )}

      <RequestDialog
        open={requestOpen}
        institutionId={institutionId}
        onClose={() => setRequestOpen(false)}
        onRequested={() => {
          setRequestOpen(false);
          reload();
        }}
      />

      <DenyDialog
        grant={denying}
        onClose={() => setDenying(null)}
        onDenied={() => {
          setDenying(null);
          reload();
        }}
      />
    </div>
  );
}

/* ─── Request ─── */

function RequestDialog({
  open,
  institutionId,
  onClose,
  onRequested,
}: {
  open: boolean;
  institutionId: string;
  onClose: () => void;
  onRequested: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<RequestSupportAccessRequest>(() => emptyRequest(institutionId));
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyRequest(institutionId));
      setAcknowledged(false);
      setError(null);
    }
  }, [open, institutionId]);

  const impersonating = form.mode === 'IMPERSONATION';
  const durationValid =
    form.durationMinutes >= MIN_SUPPORT_ACCESS_MINUTES && form.durationMinutes <= MAX_SUPPORT_ACCESS_MINUTES;
  const targetValid = impersonating ? (form.targetPersonId ?? '').trim().length > 0 : true;
  const valid =
    form.institutionId.trim().length > 0 &&
    durationValid &&
    targetValid &&
    form.reason.trim().length >= MIN_SUPPORT_REASON_LENGTH &&
    (!impersonating || acknowledged);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.supportAccess.request({
        ...form,
        institutionId: form.institutionId.trim(),
        // A target on a read-only request means the two modes have been confused,
        // and guessing which was meant is how somebody gets impersonation they
        // did not ask for. It is dropped rather than sent.
        targetPersonId: impersonating ? form.targetPersonId?.trim() : undefined,
        reason: form.reason.trim(),
        ticketReference: form.ticketReference?.trim() || undefined,
      });
      onRequested();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.support.requestTitle')}
      description={t('platform.support.requestLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.support.fieldInstitution')}</span>
          <InstitutionPicker
            id="support-request-institution"
            value={form.institutionId}
            onChange={(next) => setForm({ ...form, institutionId: next })}
          />
        </label>

        <fieldset className={styles.fieldset}>
          <legend className={styles.fieldLabel}>{t('platform.support.fieldMode')}</legend>
          {(['READ_ONLY', 'IMPERSONATION'] as const).map((mode) => (
            <label key={mode} className={styles.radioRow}>
              <input
                type="radio"
                name="support-mode"
                value={mode}
                checked={form.mode === mode}
                onChange={() => setForm({ ...form, mode: mode as SupportAccessMode, targetPersonId: '' })}
              />
              <span>
                <strong>{t(`platform.support.mode.${mode}`)}</strong>
                <span className={styles.fieldHint}>{t(`platform.support.modeHint.${mode}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {impersonating && (
          <>
            <div className={styles.warningNotice} role="note">
              <Icon name="warning" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.support.impersonationWarningTitle')}</p>
                <p className={styles.warningBody}>
                  {t('platform.support.impersonationWarningBody', {
                    roles: NON_IMPERSONABLE_ROLE_CODES.join(', '),
                  })}
                </p>
              </div>
            </div>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.support.fieldTarget')}</span>
              <input
                className={styles.input}
                value={form.targetPersonId ?? ''}
                required
                onChange={(event) => setForm({ ...form, targetPersonId: event.target.value })}
              />
              <span className={styles.fieldHint}>{t('platform.support.fieldTargetHint')}</span>
            </label>

            <Checkbox
              label={t('platform.support.impersonationAcknowledge')}
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
          </>
        )}

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.support.fieldDuration')}</span>
            <input
              className={styles.input}
              type="number"
              min={MIN_SUPPORT_ACCESS_MINUTES}
              max={MAX_SUPPORT_ACCESS_MINUTES}
              value={form.durationMinutes}
              required
              aria-invalid={!durationValid}
              onChange={(event) => setForm({ ...form, durationMinutes: Number(event.target.value) })}
            />
            <span className={styles.fieldHint}>
              {t('platform.support.fieldDurationHint', {
                min: MIN_SUPPORT_ACCESS_MINUTES,
                max: MAX_SUPPORT_ACCESS_MINUTES,
              })}
            </span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.support.fieldTicket')}</span>
            <input
              className={styles.input}
              value={form.ticketReference ?? ''}
              onChange={(event) => setForm({ ...form, ticketReference: event.target.value })}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.support.fieldReason')}</span>
          <textarea
            className={styles.textarea}
            rows={4}
            value={form.reason}
            required
            aria-invalid={form.reason.length > 0 && form.reason.trim().length < MIN_SUPPORT_REASON_LENGTH}
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
          <span className={styles.fieldHint}>
            {t('platform.support.fieldReasonHint', { min: MIN_SUPPORT_REASON_LENGTH })}
          </span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.support.request')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyRequest(institutionId: string): RequestSupportAccessRequest {
  return {
    institutionId,
    // Read-only is preselected. Impersonation has to be chosen.
    mode: 'READ_ONLY',
    targetPersonId: '',
    durationMinutes: 60,
    reason: '',
    ticketReference: '',
  };
}

/* ─── Deny ─── */

function DenyDialog({
  grant,
  onClose,
  onDenied,
}: {
  grant: SupportAccessGrant | null;
  onClose: () => void;
  onDenied: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!grant) return null;

  const run = async (reason: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.supportAccess.deny(grant.id, reason);
      onDenied();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmActionDialog
      open
      title={t('platform.support.denyTitle')}
      intro={t('platform.support.denyIntro', {
        who: grant.supportPersonName ?? grant.supportPersonId,
      })}
      tone="warning"
      impactStatus="ready"
      impact={
        <ul className={styles.impactList}>
          <li>{t('platform.support.denyImpactBlocked')}</li>
          <li>{t('platform.support.denyImpactReason')}</li>
        </ul>
      }
      confirmLabel={t('platform.support.deny')}
      submitting={submitting}
      error={error}
      onCancel={onClose}
      onConfirm={run}
    />
  );
}
